import { TableRes } from '../../../shared/types';
import { getPspRecordsByKey } from './getPspRecordsActive';
import { getAllDropdowns } from '../../../shared/services/allDropdownsRegistry';
import { getActiveValue, isActiveEqual } from './getActiveValue';

type UpdateRowFn = (args: { rowId: number | string; updatedFields: Record<string, any> }) => void;

export type PspActiveOnChangeParams = {
  rowId: number | string;
  row: Record<string, any>;
  value: any;
  updateRow: UpdateRowFn;
  tableDetails: TableRes;
  screenGridId: number;
  setRows?: (rows: Array<any> | ((prev: Array<any>) => Array<any>)) => void;
  setAllRowsData?: (rows: Array<any> | ((prev: Array<any>) => Array<any>)) => void;
  allColumns: string[];
  updateCustomConfig?: (next: any) => void;
  customConfig?: any;
  allRows?: any[];
  getAllRows?: () => any[];
};

/**
 * Pure helper functions.
 */
function keyMatchFactory(designOwnerId: any, pfLc: any, mpNr: any) {
  return (r: any) =>
    r?.design_owner_id === designOwnerId && r?.pf_lc === pfLc && String(r?.mp_nr) === String(mpNr);
}

function isActiveYesFactory(activeYes: any) {
  return (v: any) => isActiveEqual(v, activeYes);
}

function getStatusForActiveChange(currentStatus: any) {
  return currentStatus === 'N' || currentStatus === 'C' ? currentStatus : 'E';
}

function overlayLocalDeactivations(rows: any[], idsToDeactivate: Set<any>, activeNo: any) {
  return rows.map((r) => {
    if (idsToDeactivate.has(r?.id)) {
      const newStatus = getStatusForActiveChange(r?.status);
      return { ...r, active: activeNo, status: newStatus };
    }
    return r;
  });
}

function prependMissingRemoteRows(missingTargets: any[], activeNo: any) {
  return missingTargets.map((r) => ({
    ...r,
    active: activeNo,
    status: 'E',
    backup: { ...r },
  }));
}

/**
 * Fetch remote rows for the given PSP key.
 * Safely normalizes the backend response shape and fails soft (returns []) on errors.
 */
async function fetchRemoteRowsByKey(
  designOwnerId: any,
  pfLc: any,
  mpNr: any,
  screenGridId: number,
  allColumns: string[],
  tableDetails: TableRes,
): Promise<any[]> {
  try {
    const data = await getPspRecordsByKey(designOwnerId, pfLc, mpNr, screenGridId, allColumns);
    const schemaName = tableDetails?.mbdDataGrid?.schemaName;
    const tableName = tableDetails?.mbdDataGrid?.dataGridSource ?? 'psptable';

    const respData = (data as any)?.data ?? data;
    const fetchedRows: Array<any> = respData?.[`${schemaName}_${tableName}`] ?? [];
    return Array.isArray(fetchedRows) ? fetchedRows : [];
  } catch {
    return [];
  }
}

/**
 * Orchestrates "Active" flag changes for PSP rows.
 *
 * Behavior:
 * - Updates the current row's active/status.
 * - If activating, enforces exclusivity for the same PSP key:
 *   - Deactivates other locally-present active rows with the same key.
 *   - Fetches remote rows and injects any missing active ones as deactivated into customRows.
 * - Safely handles backend shape and fetch failures (falls back to local-only processing).
 *
 * Side effects:
 * - Calls updateRow() to mutate individual rows.
 * - Optionally calls updateCustomConfig() to prepend missing remote rows into ScreenSlotProps.customRows.
 *
 * @param params - Inputs required to perform the change and optional state accessors.
 * @param params.rowId - Identifier of the edited row.
 * @param params.row - Current row data; must include design_owner_id, pf_lc, mp_nr.
 * @param params.value - New value for the "active" field; compared to dropdown "yes".
 * @param params.updateRow - Callback to apply updates to individual rows.
 * @param params.tableDetails - Table metadata used to parse backend response.
 * @param params.screenGridId - Grid identifier for the fetch.
 * @param params.allColumns - Column list used by the fetch call.
 * @param params.updateCustomConfig - Optional setter to update customRows in screen config.
 * @param params.customConfig - Current custom config to be merged.
 * @param params.getAllRows - Optional getter to obtain the latest rows.
 *
 * @returns Promise<void> - Performs side effects; no direct return value.
 */
export async function pspActiveOnChange(params: PspActiveOnChangeParams): Promise<void> {
  const {
    rowId,
    row,
    value,
    updateRow,
    tableDetails,
    screenGridId,
    allColumns,
    updateCustomConfig,
    customConfig,
    getAllRows,
  } = params;

  const allDropdowns = getAllDropdowns();
  const { yes: ACTIVE_YES, no: ACTIVE_NO } = getActiveValue(allDropdowns?.active || []);

  // Ignore new rows not yet persisted
  if (row.status === 'N') {
    return;
  }

  const safeAllRows = (() => {
    try {
      const snapshot = typeof getAllRows === 'function' ? getAllRows() : [];
      return Array.isArray(snapshot) ? snapshot : [];
    } catch {
      return [];
    }
  })();

  const isActiveYes = isActiveYesFactory(ACTIVE_YES);
  const isYes = isActiveYes(value);
  const isExistingRow = !!row?.id;

  // For new rows, only set active flag and return
  if (!isExistingRow) {
    updateRow({ rowId, updatedFields: { active: isYes ? ACTIVE_YES : ACTIVE_NO } });
    return;
  }

  // For existing rows, update active and possibly status
  const currentUpdated: Record<string, any> = {
    active: isYes ? ACTIVE_YES : ACTIVE_NO,
    status: getStatusForActiveChange(row?.status),
  };
  updateRow({ rowId, updatedFields: currentUpdated });

  // If deactivating, no exclusive check needed
  if (!isYes) {
    return;
  }

  const { design_owner_id: designOwnerId, pf_lc: pfLc, mp_nr: mpNr } = row ?? {};
  if (!designOwnerId || !pfLc || !mpNr) {
    return;
  }

  const keyMatch = keyMatchFactory(designOwnerId, pfLc, mpNr);

  // Step 1: Deactivate locally-present rows with the same PSP key (except the current row)
  const localPresentTargets = safeAllRows.filter(
    (r) => keyMatch(r) && isActiveYes(r?.active) && r?.id !== row?.id && r.status !== 'N',
  );

  if (localPresentTargets.length) {
    for (const fetched of localPresentTargets) {
      const newStatus = getStatusForActiveChange(fetched?.status);
      updateRow({ rowId: fetched.id, updatedFields: { active: ACTIVE_NO, status: newStatus } });
    }
  }

  // Step 2: Fetch remote rows and deactivate any remote actives not present locally
  const fetchedRows = await fetchRemoteRowsByKey(
    designOwnerId,
    pfLc,
    mpNr,
    screenGridId,
    allColumns,
    tableDetails,
  );

  const remoteTargets = fetchedRows.filter(
    (r) => keyMatch(r) && isActiveYes(r?.active) && r?.id !== row?.id,
  );

  if (!remoteTargets.length) {
    return;
  }

  const allIds = new Set(safeAllRows.map((r) => r?.id));
  const missingTargets = remoteTargets.filter((t) => !allIds.has(t?.id));

  // If there are backend actives missing on the frontend, prepend them as deactivated
  // Note: We must also overlay the local deactivations onto the snapshot BEFORE building the next rows
  // because safeAllRows was captured before we changed some rows to active: 0.
  if (updateCustomConfig && missingTargets.length) {
    // Avoid duplicates: exclude any rows with the same ids as the new missing targets
    const newMissingTargets = prependMissingRemoteRows(missingTargets, ACTIVE_NO);

    const missingIds = new Set(missingTargets.map((t) => t?.id));
    const locallyDeactivatedIds = new Set(localPresentTargets.map((r) => r?.id));

    // Overlay local deactivation state onto the snapshot
    const safeAllRowsWithLocalOverlays = overlayLocalDeactivations(
      safeAllRows,
      locallyDeactivatedIds,
      ACTIVE_NO,
    );

    // Build the next rows by prepending backend-missing rows, then the updated snapshot
    const filteredSafeAllRows = safeAllRowsWithLocalOverlays.filter((r) => !missingIds.has(r?.id));
    const nextRows = [...newMissingTargets, ...filteredSafeAllRows];

    // Update the custom config to reflect the merged/overlayed rows
    updateCustomConfig({
      ...(customConfig ?? {}),
      ScreenSlotProps: {
        ...(customConfig?.ScreenSlotProps ?? {}),
        customRows: nextRows,
      },
    });
  }
}
